import { Box, Button, type DialogProps } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAddToRole } from '~/api/roles';
import { Count } from '~/components/count';
import { SeparatedDataGrid } from '~/components/data-grid';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib';
import { SearchBar } from '~/components/forms/search-bar';
import { ListCheckbox } from '~/components/list-row';
import { searchRegex } from '~/lib/search';
import type {
  RoleDataSourceList__DataSource as DataSource,
  RoleDataSourceList__Role as Role,
} from '../RoleDataSourceList.generated';

export interface AddDataSourcesDialogProps extends Omit<DialogProps, 'role'> {
  dataSources: readonly DataSource[];
  role: Role;
}

export const AddDataSourcesDialog = ({
  dataSources,
  role,
  ...props
}: AddDataSourcesDialogProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);

  const [add, { called }] = useAddToRole({
    variables: {
      roleId: role.id,
      dataSourceRules: selectedIds.map((id) => ({ dataSourceId: id as number })),
    },
  });

  const roleIds = new Set(role.dataSourceRules.map((x) => x.dataSource.id));

  const searchReg = searchRegex(search);

  const rows = dataSources.filter((x) => !roleIds.has(x.id) && searchReg.test(x.name));

  const close = () => props.onClose?.({}, 'backdropClick');

  return (
    <Dialog {...props}>
      <DialogTitle onClose={close}>{t('pages.roles.dialogs.add_data_sources.title')}</DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <DialogContentTitle>
          <Trans
            i18nKey="pages.roles.dialogs.add_data_sources.instruction"
            values={{ role_name: role.name }}
            components={{ b: <b /> }}
          />
        </DialogContentTitle>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <SearchBar
            onChange={setSearch}
            placeholder={t('pages.roles.dialogs.add_data_sources.search_placeholder')}
            search={search}
          />
          <Count
            selectedCount={selectedIds.length}
            totalCount={rows.length}
            thing="pages.roles.data_sources.thing_plural"
          />
        </Box>

        <Box>
          <SeparatedDataGrid
            checkboxSelection
            columns={[{ field: 'name', headerName: t('common.labels.name'), flex: 1 }]}
            onRowSelectionModelChange={setSelectedIds}
            rows={rows}
            rowSelectionModel={selectedIds}
            slots={{
              baseCheckbox: ListCheckbox,
              columnResizeIcon: () => null,
              noRowsOverlay: () => {
                return (
                  <Box height="100%" display="flex" justifyContent="center" alignItems="center">
                    {t('pages.roles.dialogs.add_data_sources.no_data_sources')}
                  </Box>
                );
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button disabled={called} onClick={close} variant="outlined">
          {t('common.dialogs.cancel')}
        </Button>

        <Button
          color="primary"
          disabled={called || !selectedIds.length}
          onClick={() => {
            close();
            return add();
          }}
          variant="contained"
        >
          {t('common.dialogs.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
